import { Dialog, DialogContent } from '@mui/material'
import { EventLogDto, WasteContainerDto } from '@services/dtos'
import { DataGridPro, GridColumns } from '@mui/x-data-grid-pro'
import { useEffect, useMemo } from 'react'
import moment from 'moment'
import { WasteContainerEventLabels } from '@utils/constants'
import { FormHeader } from '../forms/FormHeader'
import { getContainerSubTitles } from '@utils/waste.utils'
import { UpTransition } from '../animations'
import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks'
import { getWasteContainerLogs, setWasteContainerLogsFilters } from '@state/reducers/wasteContainerReducer'
import { PaginatedDto } from '@/services/extendedType'

const PAGE_SIZE = 20

type Props = {
  container: WasteContainerDto
  onClose: () => void
}

export function WasteContainerLogsDialog({ container, onClose }: Props) {
  const dispatch = useAppDispatch()
  const logs = useAppSelector((state) => (state.wasteContainer as any).logs as PaginatedDto<EventLogDto> | undefined)
  const logsLoading = useAppSelector((state) => (state.wasteContainer as any).logsLoading as boolean | undefined)
  const logsFilters = useAppSelector((state) => (state.wasteContainer as any).logsFilters as { page?: number } | undefined)

  useEffect(() => {
    dispatch(setWasteContainerLogsFilters({ containerId: container.id, page: 0, limit: PAGE_SIZE }))
    dispatch(getWasteContainerLogs())
    return () => {
      dispatch(setWasteContainerLogsFilters({ containerId: undefined }))
    }
  }, [container.id])

  const columns: GridColumns<EventLogDto> = useMemo(
    () => [
      {
        field: 'createdAt',
        headerName: 'Date',
        width: 145,
        valueFormatter: ({ value }) => moment(value).format('DD/MM/YYYY HH:mm'),
      },
      {
        field: 'event',
        headerName: 'Action',
        width: 205,
        valueFormatter: ({ value }) => WasteContainerEventLabels[value] ?? value,
      },
      {
        field: 'createdBy',
        headerName: 'Par',
        width: 180,
        valueFormatter: ({ value }) => value ? `${value.firstName} ${value.lastName}` : '',
      },
      {
        field: 'wasteEntry',
        headerName: 'Déchet',
        width: 220,
        valueFormatter: ({ value }) => {
          if (!value) {
            return null
          }
          const { preparationLot, generatorElution, label, no } = value
          if (preparationLot) {
            return `#${no} Préparation`
          }
          if (generatorElution) {
            return `#${no} Élution`
          }
          return `#${no} Ajout libre - ${label}`
        }
      },
      {
        field: 'data',
        headerName: 'Données',
        width: 300,
        valueFormatter: ({ value }) => {
          if (!value) {
            return null
          }
          const labelMap: Record<string, string> = {
            no: 'N°',
            activityRefAt: 'Heure mesure',
            activityAtRefMbq: 'Activité (MBq)',
            originContainerNo: 'Conteneur d\'origine',
            targetContainerNo: 'Conteneur cible',
          }
          return Object.entries(value)
            .map(([key, val]) => {
              const formattedKey = labelMap[key] || key
              const formattedValue =
                key === 'activityRefAt' && val
                  ? moment(val as moment.MomentInput).format('DD/MM/YYYY HH:mm')
                  : val
              return `${formattedKey}: ${formattedValue}`
            })
            .join(', ')
        }
      }
    ],
    [],
  )

  return (
    <Dialog
      open
      fullWidth
      maxWidth="lg"
      PaperProps={{ sx: { height: '80vh' } }}
      TransitionComponent={UpTransition}
      onClose={onClose}
    >
      <div className="px-12 pt-6">
        <FormHeader
          title={`Historique #${container.no}`}
          subTitles={getContainerSubTitles(container)}
          onClose={onClose}
          isSubmitting={false}
          showSubmit={false}
        />
      </div>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', pt: 1, overflow: 'hidden' }}>
        <DataGridPro
          density="compact"
          pagination
          paginationMode="server"
          page={logsFilters?.page ?? 0}
          pageSize={PAGE_SIZE}
          rowsPerPageOptions={[PAGE_SIZE]}
          onPageChange={(page) => {
            dispatch(setWasteContainerLogsFilters({ page }))
            dispatch(getWasteContainerLogs())
          }}
          rows={logs?.datas ?? []}
          rowCount={logs?.totalCount ?? 0}
          columns={columns}
          loading={logsLoading ?? false}
          disableSelectionOnClick
          disableMultipleSelection
          sx={{ backgroundColor: 'white', borderRadius: '15px 15px 25px 25px' }}
        />
      </DialogContent>
    </Dialog>
  )
}
